import React, { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { getLocale, useIntl } from 'umi';
import { Bubble, PagingQuery } from 'tea-component';
import { LANGUAGE_CN } from '@home/_config/config';
import { buildRobloxGenreLabelMap } from '@intelligence/v3/utils/robloxGenreLabel';
import TopchartTable from '@intelligence/components/TableComponents/TopchartTable';
import FirstRow from '@intelligence/components/TableComponents/components/FirstRow';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@intelligence/components/TableComponents/components/TableCheckbox';

import type {
  TableSelectionType,
  TableSelectStatusType,
} from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type { IMainGenre } from '@intelligence/types/common';
import type { IRobloxTopChartsRow } from '../../types';
import {
  getRobloxTopChartsExtraColumns,
  getRobloxTopChartsMetricColumns,
} from './columns';
import './index.scss';

const CHECKBOX_SLOT_WIDTH = 36;
const CHECKBOX_RANK_GAP = 10;
const RANK_TEXT_WIDTH = 45;
const RANK_SELECT_COLUMN_WIDTH = CHECKBOX_SLOT_WIDTH + CHECKBOX_RANK_GAP + RANK_TEXT_WIDTH;
const GAMETITLE_COLUMN_WIDTH = 330;

interface IRobloxTopChartsTableProps {
  loading: boolean;
  records: IRobloxTopChartsRow[];
  total: number;
  pageIndex: number;
  pageSize: number;
  tableSelection: TableSelectionType[];
  tableSelectionAll: TableSelectionType[];
  sortKey: string;
  sortOrder: 'asc' | 'desc';
  onPagingChange: (paging: PagingQuery) => void;
  onCheckGame: (list: TableSelectionType[], status: TableSelectStatusType) => void;
  onSortChange: (sortItem: SortByItemType) => void;
  robloxGenreOptions?: IMainGenre[];
}

const RobloxTopChartsTable: React.FC<IRobloxTopChartsTableProps> = (props) => {
  const {
    loading,
    records,
    total,
    pageIndex,
    pageSize,
    tableSelection,
    tableSelectionAll,
    sortKey,
    sortOrder,
    onPagingChange,
    onCheckGame,
    onSortChange,
    robloxGenreOptions = [],
  } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isChinese = locale === LANGUAGE_CN;
  const robloxGenreLabelMap = useMemo(
    () => buildRobloxGenreLabelMap(robloxGenreOptions),
    [robloxGenreOptions],
  );
  const [selectList, setSelectList] = useState(tableSelection);
  const [showShadow, setShowShadow] = useState(false);

  useEffect(() => {
    setSelectList(tableSelection);
  }, [tableSelection]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const tableNode = document.querySelector(
      '.dbi-roblox-top-charts-table-wrap .dbi-roblox-top-charts-table.tea-table > .tea-table__body',
    ) as HTMLElement | null;

    if (!tableNode) {
      return;
    }

    const handleScroll = (): void => {
      setShowShadow(tableNode.scrollLeft > 0);
    };

    handleScroll();
    tableNode.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      tableNode.removeEventListener('scroll', handleScroll);
    };
  }, [loading, records]);

  const handleCheckChange = (
    list: TableSelectionType[],
    status: TableSelectStatusType,
  ): void => {
    setSelectList(list);
    onCheckGame(list, status);
  };

  const sortOptions = useMemo(
    () => ({
      currentSortKey: sortKey,
      currentSortValue: sortOrder,
      onSortChange,
    }),
    [sortKey, sortOrder, onSortChange],
  );

  const metricColumns = useMemo(
    () => getRobloxTopChartsMetricColumns(formatMessage, sortOptions),
    [formatMessage, sortOptions],
  );

  const extraColumns = useMemo(
    () => getRobloxTopChartsExtraColumns(formatMessage, {
      robloxGenreLabelMap,
      isChinese,
    }),
    [formatMessage, robloxGenreLabelMap, isChinese],
  );

  return (
    <div
      className={classnames('dbi-roblox-top-charts-table-wrap', {
        'table-sticky-two-shadow': showShadow,
      })}
    >
      <TopchartTable
        className="dbi-roblox-top-charts-table"
        loading={loading}
        recordKey="game_id"
        records={records}
        tableTotal={total}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        columns={[
          {
            key: 'rank_select',
            width: RANK_SELECT_COLUMN_WIDTH,
            header: () => (
              <div className="dbi-roblox-rank-col">
                <div className="dbi-roblox-rank-col__checkbox">
                  <TableCheckboxHeader
                    selectList={selectList}
                    tableSelectionAll={tableSelectionAll}
                    disabled={loading || records.length === 0}
                    onChange={handleCheckChange}
                  />
                </div>
                <div className="dbi-text-id">#</div>
              </div>
            ),
            render: (row: IRobloxTopChartsRow, _rowKey: string, recordIndex: number) => (
              <div className="dbi-roblox-rank-col">
                <div className="dbi-roblox-rank-col__checkbox">
                  <TableCheckboxRender
                    selectList={selectList}
                    cvm={{ ...(row || {}), gameId: row?.game_id || '' } as TableSelectionType}
                    onChange={handleCheckChange}
                  />
                </div>
                <span className="dbi-text-id">
                  {(pageIndex - 1) * pageSize + recordIndex + 1}
                </span>
              </div>
            ),
          },
          {
            key: 'gametitle',
            header: (
              <div className="dbi-roblox-gametitle-col">
                {formatMessage({ id: 'dbi_top_gametitle' })}
              </div>
            ),
            width: GAMETITLE_COLUMN_WIDTH,
            render: (row: IRobloxTopChartsRow) => {
              const gameTitle = row?.entity_name || row?.game_id || '-';

              return (
                <div className="dbi-roblox-gametitle-col">
                  <FirstRow
                    className="dbi-roblox-gametitle-first-row"
                    cvm={{
                      img: row?.cover || '',
                      gametitle: gameTitle,
                      text: row?.publisher || '-',
                    }}
                    renderDom={
                      <Bubble dark arrowPointAtCenter content={gameTitle} closeOnScroll>
                        <span className="dbi-roblox-gametitle-first-row__name">{gameTitle}</span>
                      </Bubble>
                    }
                  />
                </div>
              );
            },
          },
          ...metricColumns,
          ...extraColumns,
        ]}
      />
    </div>
  );
};

export default RobloxTopChartsTable;
